import React from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import PageShell from "../components/PageShell";
import Hero from "../components/Hero";
import HeroCanvas from "../components/HeroCanvas";
import HeroCanvasParticles from "../components/heroes/HeroCanvasParticles";
import HeroCanvasWireframe from "../components/heroes/HeroCanvasWireframe";
import HeroCanvasLiquid from "../components/heroes/HeroCanvasLiquid";
import HeroCanvasShards from "../components/heroes/HeroCanvasShards";

export const HERO_VARIANTS = [
  {
    slug: "hero-1",
    name: "Torus Knot",
    short: "Original — chrome torus knot + orbiting shards.",
    long: "A liquid-metal torus knot anchored center-stage with orbiting metallic icosahedra. The default Ulties hero. Premium, technical, calm.",
    Canvas: HeroCanvas,
    tone: "Confident · Premium",
  },
  {
    slug: "hero-2",
    name: "Particle Wave",
    short: "Topographic dot field that reacts to your cursor.",
    long: "An 80×48 buffer-geometry point field rippling on a 3-axis sine wave. The cursor creates a radial disturbance — Apple keynote feel.",
    Canvas: HeroCanvasParticles,
    tone: "Spacious · Editorial",
  },
  {
    slug: "hero-3",
    name: "Wireframe Sphere",
    short: "Double-layer wireframe icosahedron with metal core.",
    long: "An outer wireframe icosahedron wraps a ghost layer and a polished metallic sphere. Tech, defensive, sharp.",
    Canvas: HeroCanvasWireframe,
    tone: "Technical · Defensive",
  },
  {
    slug: "hero-4",
    name: "Liquid Metal",
    short: "Heavy-distort blob with chromatic rim lights.",
    long: "A high-distort metallic blob lit by violet + cyan rim lights. Pulses subtly on a slow sine. Bolder, more sculptural.",
    Canvas: HeroCanvasLiquid,
    tone: "Bold · Sculptural",
  },
  {
    slug: "hero-5",
    name: "Glass Shards",
    short: "Translucent floating panels with real transmission.",
    long: "Nine thin glass shards suspended in space, lit through with transmission + clearcoat. Apple Vision-Pro keynote vibe.",
    Canvas: HeroCanvasShards,
    tone: "Editorial · Architectural",
  },
];

const BY_SLUG = Object.fromEntries(HERO_VARIANTS.map((v) => [v.slug, v]));

/** /heroes — gallery picker showing all 5 hero variants. */
export function HeroesIndex() {
  return (
    <PageShell>
      <section className="pt-32 md:pt-40 pb-12 max-w-7xl mx-auto px-6 md:px-10">
        <span className="text-[11px] tracking-[0.28em] uppercase text-muted-x font-mono-x">
          ◇ Hero gallery
        </span>
        <h1 className="mt-4 font-display text-5xl md:text-7xl font-bold tracking-[-0.04em] leading-[0.95] text-primary-x">
          Pick a 3D <em className="not-italic text-muted-x">hero.</em>
        </h1>
        <p className="mt-6 text-secondary-x text-lg max-w-xl leading-relaxed">
          Five immersive Three.js heroes, all theme-aware and tuned for premium dark layouts. Open one to preview it full-bleed with the rest of the home page copy.
        </p>
      </section>

      <section className="pb-28 md:pb-40 max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-2 gap-6 md:gap-10">
          {HERO_VARIANTS.map((v, i) => (
            <motion.div
              key={v.slug}
              initial={{ y: 30, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ delay: i * 0.06, duration: 0.7 }}
              data-testid={`hero-thumb-${v.slug}`}
            >
              <Link
                to={`/${v.slug}`}
                className="group block"
              >
                <div className="relative aspect-[16/10] overflow-hidden rounded-2xl border border-hairline bg-surface-2">
                  <div className="absolute inset-0">
                    <div className="absolute inset-0 scale-[0.78] origin-center pointer-events-none">
                      <v.Canvas />
                    </div>
                  </div>
                  <div className="absolute top-5 left-5 right-5 flex items-center justify-between text-[11px] tracking-[0.25em] uppercase font-mono-x text-secondary-x">
                    <span>{v.slug.toUpperCase()}</span>
                    <span>{v.tone}</span>
                  </div>
                  <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between gap-4">
                    <div>
                      <div className="font-display text-3xl md:text-4xl font-medium tracking-tight text-primary-x">
                        {v.name}
                      </div>
                      <p className="mt-2 text-secondary-x text-sm max-w-xs">{v.short}</p>
                    </div>
                    <span
                      className="shrink-0 inline-flex items-center justify-center h-10 w-10 rounded-full opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all"
                      style={{ background: "var(--text-primary)", color: "var(--canvas)" }}
                    >
                      →
                    </span>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>
    </PageShell>
  );
}

/** /hero-1 ... /hero-5 — full-bleed preview of a single hero. */
export function HeroVariantPage() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\//, "");
  const v = BY_SLUG[slug] || HERO_VARIANTS[0];

  return (
    <PageShell>
      <Hero Canvas={v.Canvas} />

      {/* Picker dock */}
      <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-40">
        <div
          className="flex items-center gap-1 rounded-full border border-hairline-strong bg-canvas/80 backdrop-blur-2xl px-2 py-1.5"
          data-testid="hero-picker"
        >
          {HERO_VARIANTS.map((x) => {
            const active = x.slug === v.slug;
            return (
              <Link
                key={x.slug}
                to={`/${x.slug}`}
                data-testid={`hero-picker-${x.slug}`}
                title={x.name}
                className={`relative px-3 py-1.5 text-xs rounded-full transition-colors ${
                  active ? "" : "text-secondary-x hover:text-primary-x"
                }`}
                style={
                  active
                    ? { background: "var(--text-primary)", color: "var(--canvas)" }
                    : {}
                }
              >
                {x.name}
              </Link>
            );
          })}
          <Link
            to="/heroes"
            data-testid="hero-picker-all"
            className="ml-1 px-3 py-1.5 text-xs text-muted-x hover:text-primary-x transition-colors"
          >
            ◇ All
          </Link>
        </div>
      </div>

      {/* Below-hero description */}
      <section className="border-t border-hairline py-24 md:py-32 bg-surface">
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-center">
          <span className="text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x">
            {v.slug.toUpperCase()} · {v.tone}
          </span>
          <h2 className="mt-6 font-display text-5xl md:text-6xl font-medium tracking-tight text-primary-x">
            {v.name}
          </h2>
          <p className="mt-6 text-secondary-x text-lg leading-relaxed">{v.long}</p>
          <Link
            to="/heroes"
            className="mt-10 inline-flex items-center gap-2 rounded-full border border-hairline-strong px-5 py-2.5 text-sm text-primary-x hover-glass transition-colors"
          >
            ← Back to gallery
          </Link>
        </div>
      </section>
    </PageShell>
  );
}
